/**
 * Module Toit - Point d'entrée principal
 * Conforme au cahier des charges - Module 4: Le Toit de la Tonnelle
 * 
 * ARCHITECTURE MODULAIRE:
 * - Exportation centralisée de tous les composants
 * - Services de génération 3D et Canvas 2D
 * - Types TypeScript complets
 * - API de communication serveur
 * - Configuration par défaut
 * 
 * GÉNÉRATION LOCALE OBLIGATOIRE:
 * - Élimination des clignotements
 * - Suppression des résidus fantômes
 * - Réactivité instantanée
 * - Expérience utilisateur fluide
 */

// Imports pour l'export par défaut
import { Toit3DService } from './services/Toit3DService';
import { Canvas2DService } from './services/Canvas2DService';
import { toitAPI } from './api';
import { ToitModule } from './components/ToitModule';
import { TypeMateriauToit, TypeStyleBaleine, InfosMateriauToit, ParametresToit, TypePointJonction, ConfigurationModuleToit } from './types';

// Types
export type {
  // Types de base
  Point2D,
  Point3D,
  CourbeBezier,
  LigneDroite,
  
  // Structures de données
  ProfilBaleinePersonnalise,
  ParametresToit,
  GeometrieBaleine,
  StructureToit,
  DonneesStructureBasePourToit,
  CalculLongueurBaleine,
  
  // Configuration et état
  StyleBaleinePredefini,
  ConfigurationCanvas2D,
  EtatSelection,
  EvenementCanvas,
  EtatModuleToit,
  ConfigurationModuleToit
} from './types';

// Énumérations (valeurs)
export {
  TypeStyleBaleine,
  TypeMateriauToit,
  TypePointJonction,
  OutilDessin
} from './types';

// Services
export { Toit3DService } from './services/Toit3DService';
export { Canvas2DService } from './services/Canvas2DService';

// API
export { ToitAPI } from './api';
export { toitAPI } from './api';

// Composants
export { ToitModule } from './components/ToitModule';
export { default as ToitModuleDefault } from './components/ToitModule';

// Configuration par défaut du module
export const TOIT_MODULE_CONFIG: ConfigurationModuleToit = {
  nom: 'Toit',
  version: '1.0.0',
  description: 'Module de génération du toit pour tonnelles 3D',
  parametresDefaut: {
    actif: false, // Désactivé par défaut
    styleBaleine: TypeStyleBaleine.COURBE_DOUCE,
    nombreBaleines: 4, // Sera ajusté selon le nombre de montants
    hauteurToit: 1.2, // Hauteur générale du toit
    hauteurConvergence: 1.5,
    pointJonction: TypePointJonction.POINT_SIMPLE,
    rayonPointJonction: 0.1,
    aplatirBaleines: true, // Aplatissement activé par défaut
    alignerSurMontants: false // Désactivé par défaut
  },
  canvas: {
    largeur: 400,
    hauteur: 300,
    tailleGrille: 10,
    couleurs: {
      grille: '#e9ecef',
      ligne: '#007bff',
      bezier: '#28a745',
      controle: '#ffc107',
      fond: '#f8f9fa'
    }
  },
  stylesPredefinis: []
};

// Contraintes et limites séparées
export const TOIT_CONTRAINTES = {
  nombreBaleinesMin: 3,
  nombreBaleinesMax: 20,
  hauteurToitMin: 0.1,
  hauteurToitMax: 1.5,
  hauteurConvergenceMin: 0.1,
  hauteurConvergenceMax: 1.5,
  epaisseurMin: 0.05,
  epaisseurMax: 0.3,
  rayonJonctionMin: 0.05,
  rayonJonctionMax: 0.5
};

/**
 * Fonction utilitaire pour créer des paramètres de toit avec des valeurs par défaut
 */
export function creerParametresToitParDefaut(
  nombreMontants: number,
  overrides?: Partial<ParametresToit>
): ParametresToit {
  return {
    ...TOIT_MODULE_CONFIG.parametresDefaut,
    nombreBaleines: nombreMontants, // Adapter au nombre de montants
    ...overrides
  } as ParametresToit;
}

/**
 * Fonction utilitaire pour valider les paramètres du toit
 */
export function validerParametresToit(
  parametres: ParametresToit,
  nombreMontants: number
): { valide: boolean; erreurs: string[]; avertissements: string[] } {
  const erreurs: string[] = [];
  const avertissements: string[] = [];
  
  // Validation du nombre de baleines
  if (parametres.nombreBaleines && parametres.nombreBaleines < nombreMontants) {
    erreurs.push(`Le nombre de baleines (${parametres.nombreBaleines}) ne peut pas être inférieur au nombre de montants (${nombreMontants})`);
  }
  
  if (parametres.nombreBaleines && parametres.nombreBaleines < TOIT_CONTRAINTES.nombreBaleinesMin) {
    erreurs.push(`Le nombre de baleines doit être au moins ${TOIT_CONTRAINTES.nombreBaleinesMin}`);
  }
  
  if (parametres.nombreBaleines && parametres.nombreBaleines > TOIT_CONTRAINTES.nombreBaleinesMax) {
    erreurs.push(`Le nombre de baleines ne peut pas dépasser ${TOIT_CONTRAINTES.nombreBaleinesMax}`);
  }
  
  // Validation de la hauteur du toit
  if (parametres.hauteurToit < TOIT_CONTRAINTES.hauteurToitMin) {
    erreurs.push(`La hauteur du toit doit être au moins ${TOIT_CONTRAINTES.hauteurToitMin}m`);
  }
  
  if (parametres.hauteurToit > TOIT_CONTRAINTES.hauteurToitMax) {
    erreurs.push(`La hauteur du toit ne peut pas dépasser ${TOIT_CONTRAINTES.hauteurToitMax}m`);
  }
  
  // Validation de la hauteur de convergence
  if (parametres.hauteurConvergence < TOIT_CONTRAINTES.hauteurConvergenceMin) {
    erreurs.push(`La hauteur de convergence doit être au moins ${TOIT_CONTRAINTES.hauteurConvergenceMin}m`);
  }
  
  if (parametres.hauteurConvergence > TOIT_CONTRAINTES.hauteurConvergenceMax) {
    erreurs.push(`La hauteur de convergence ne peut pas dépasser ${TOIT_CONTRAINTES.hauteurConvergenceMax}m`);
  }
  
  // Note: L'épaisseur est maintenant validée au niveau de la structure de base
  
  // Validation du rayon de jonction
  if (parametres.pointJonction !== TypePointJonction.POINT_SIMPLE) {
    if (!parametres.rayonPointJonction) {
      erreurs.push('Le rayon du point de jonction est requis pour ce type de jonction');
    } else {
      if (parametres.rayonPointJonction < TOIT_CONTRAINTES.rayonJonctionMin) {
        erreurs.push(`Le rayon de jonction doit être au moins ${TOIT_CONTRAINTES.rayonJonctionMin}m`);
      }
      
      if (parametres.rayonPointJonction > TOIT_CONTRAINTES.rayonJonctionMax) {
        erreurs.push(`Le rayon de jonction ne peut pas dépasser ${TOIT_CONTRAINTES.rayonJonctionMax}m`);
      }
    }
  }
  
  // Avertissements
  if (parametres.nombreBaleines && parametres.nombreBaleines > nombreMontants * 2) {
    avertissements.push('Un nombre élevé de baleines peut affecter les performances');
  }
  
  if (parametres.hauteurConvergence > 2.5) {
    avertissements.push('Une hauteur de convergence élevée peut créer une structure instable');
  }
  
  if (parametres.styleBaleine === TypeStyleBaleine.PERSONNALISE && !parametres.profilPersonnalise) {
    avertissements.push('Aucun profil personnalisé défini, utilisation du style classique par défaut');
  }
  
  return {
    valide: erreurs.length === 0,
    erreurs,
    avertissements
  };
}

/**
 * Fonction utilitaire pour calculer les dimensions recommandées
 */
export function calculerDimensionsRecommandees(
  largeurTonnelle: number,
  longueurTonnelle: number,
  hauteurTonnelle: number
): {
  hauteurConvergenceRecommandee: number;
  epaisseurRecommandee: number;
  nombreBaleinesRecommande: number;
} {
  // Calculs basés sur les dimensions de la tonnelle
  const diagonale = Math.sqrt(largeurTonnelle ** 2 + longueurTonnelle ** 2);
  const surface = largeurTonnelle * longueurTonnelle;
  
  // Hauteur de convergence : 20-30% de la diagonale
  const hauteurConvergenceRecommandee = Math.max(
    TOIT_CONTRAINTES.hauteurConvergenceMin,
    Math.min(
      TOIT_CONTRAINTES.hauteurConvergenceMax,
      diagonale * 0.25
    )
  );
  
  // Épaisseur : proportionnelle à la surface
  const epaisseurRecommandee = Math.max(
    TOIT_CONTRAINTES.epaisseurMin,
    Math.min(
      TOIT_CONTRAINTES.epaisseurMax,
      0.05 + (surface / 100) * 0.02
    )
  );
  
  // Nombre de baleines : basé sur le périmètre
  const perimetre = 2 * (largeurTonnelle + longueurTonnelle);
  const nombreBaleinesRecommande = Math.max(
    TOIT_CONTRAINTES.nombreBaleinesMin,
    Math.min(
      TOIT_CONTRAINTES.nombreBaleinesMax,
      Math.ceil(perimetre / 2) // Une baleine tous les 2 mètres environ
    )
  );
  
  return {
    hauteurConvergenceRecommandee: Math.round(hauteurConvergenceRecommandee * 10) / 10,
    epaisseurRecommandee: Math.round(epaisseurRecommandee * 100) / 100,
    nombreBaleinesRecommande
  };
}

/**
 * Fonction utilitaire pour convertir les couleurs
 */
export function convertirCouleur(couleur: string | number): {
  hex: string;
  rgb: { r: number; g: number; b: number };
  numerique: number;
} {
  let hex: string;
  let numerique: number;
  
  if (typeof couleur === 'string') {
    hex = couleur.startsWith('#') ? couleur : `#${couleur}`;
    numerique = parseInt(hex.replace('#', ''), 16);
  } else {
    numerique = couleur;
    hex = `#${couleur.toString(16).padStart(6, '0')}`;
  }
  
  const r = (numerique >> 16) & 255;
  const g = (numerique >> 8) & 255;
  const b = numerique & 255;
  
  return {
    hex,
    rgb: { r, g, b },
    numerique
  };
}

/**
 * Fonction utilitaire pour obtenir les informations du matériau
 */
export function obtenirInfosMateriauToit(type: TypeMateriauToit): InfosMateriauToit {
  switch (type) {
    case TypeMateriauToit.FER_A_BETON:
      return {
        nom: 'Fer à béton',
        densite: 7850,
        resistanceTraction: 400,
        moduleElasticite: 200000,
        couleur: '#8C8C8C'
      };

    case TypeMateriauToit.TUBE_CARRE:
      return {
        nom: 'Tube carré',
        densite: 7850,
        resistanceTraction: 350,
        moduleElasticite: 200000,
        couleur: '#A0A0A0'
      };

    default:
      return {
        nom: 'Fer à béton',
        densite: 7850,
        resistanceTraction: 400,
        moduleElasticite: 200000,
        couleur: '#8C8C8C'
      };
  }
}

// Export par défaut du module complet
export default {
  // Composants
  ToitModule,
  
  // Services
  Toit3DService,
  Canvas2DService,
  
  // API
  toitAPI,
  
  // Configuration
  TOIT_MODULE_CONFIG,
  
  // Utilitaires
  creerParametresToitParDefaut,
  validerParametresToit,
  calculerDimensionsRecommandees,
  convertirCouleur,
  obtenirInfosMateriauToit
};